import React from 'react';
import { DoctoralNirdOverview, DoctoralNirdSlot } from '../types';
import { Card, DocRow, Hero, Kpis } from './dp120Parts';

interface DoctoralNirdViewProps {
  data: DoctoralNirdOverview | null;
  onSendToAdvisor?: (semester: number) => void;
  onUploadReport?: (semester: number, file: File) => void;
}

const COLORS = ['#42d8ff', '#42d8ff', '#4ce2bf', '#4ce2bf', '#9b7cff', '#9b7cff'];

function slotFor(data: DoctoralNirdOverview | null, semester: number): DoctoralNirdSlot {
  const found = data?.slots?.find((item) => item.semester === semester);
  return (
    found || {
      id: `nird-${semester}`,
      semester,
      course_year: Math.ceil(semester / 2),
      advisor_status: 'В РАБОТЕ',
      attestation_status: 'В РАБОТЕ',
      linked_publications_count: 0,
    }
  );
}

export const DoctoralNirdView: React.FC<DoctoralNirdViewProps> = ({
  data,
  onSendToAdvisor,
  onUploadReport,
}) => {
  const slots = [1, 2, 3, 4, 5, 6].map((semester) => slotFor(data, semester));
  const reports = data?.nird_loaded_count ?? slots.filter((item) => item.report_file).length;
  const materials = data?.materials_loaded_count ?? slots.filter((item) => item.materials_file).length;
  const risks = data?.risks_count ?? slots.filter((item) => !item.report_file).length;

  return (
    <>
      <Hero
        kicker="RESEARCH WORK · 6 SEMESTERS"
        title="НИРД и аттестация"
        text="Каждый семестр — самостоятельный слот: отчёт, подтверждающие результаты, отправка и проверка. Публикации связываются из раздела публикаций."
        color="#9b7cff"
      />
      <Kpis
        items={[
          ['НИРД', `${reports}/6`, 'Отчёты загружены'],
          ['Подтверждения', `${materials}/6`, 'Есть материалы'],
          ['Аттестация', 'По семестрам', 'Сводится из фактических данных'],
          ['Риски', String(risks), 'Нет отчёта'],
        ]}
      />
      <div className="dp120-nird">
        {slots.map((slot, index) => (
          <article key={slot.semester} className="dp120-sem-card" style={{ ['--s' as string]: COLORS[index] }}>
            <h4>
              {slot.course_year} курс · НИРД-{slot.semester}
            </h4>
            <p>Семестр {slot.semester} · отчёт + подтверждающие результаты</p>
            <DocRow
              title={`Отчёт НИРД · семестр ${slot.semester}`}
              file={slot.report_file}
              onPick={onUploadReport ? (file) => onUploadReport(slot.semester, file) : undefined}
            />
            <div style={{ height: 7 }} />
            <DocRow title={`Подтверждающие материалы НИРД-${slot.semester}`} file={slot.materials_file} />
            <div className="dp120-hero-actions">
              <button
                type="button"
                className="dp120-mini violet"
                onClick={() => onSendToAdvisor?.(slot.semester)}
              >
                Отправить Advisor
              </button>
            </div>
          </article>
        ))}
      </div>
      <div style={{ height: 11 }} />
      <Card title="Аттестационная готовность" sub="Считается из НИРД, ИУП и обязательных этапов." span="span12">
        <div className="dp120-list">
          {(data?.attestation_rows?.length
            ? data.attestation_rows
            : slots.map((slot) => ({
                semester: slot.semester,
                status_label: slot.report_file && slot.materials_file ? 'Комплект НИРД присутствует' : 'Нужны материалы НИРД',
                badge_label: slot.report_file && slot.materials_file ? 'ГОТОВО' : 'В РАБОТЕ',
                is_ready: Boolean(slot.report_file && slot.materials_file),
              }))
          ).map((row) => (
            <div key={row.semester} className="dp120-row">
              <span>
                <strong>Семестр {row.semester}</strong>
                <small>{row.status_label}</small>
              </span>
              <span className={`dp120-badge ${row.is_ready ? 'good' : 'warn'}`}>{row.badge_label}</span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};
